import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { publicQueue } from "../../api";

const POLL_INTERVAL = 10000;

// ── Mode-aware rendering config ───────────────────────────────────────────────
const MODE_CONFIG = {
  none: {
    fontSize: "72px", highContrast: false, audioAlert: false, visualFlash: false,
  },
  simple: {
    fontSize: "96px", highContrast: true, audioAlert: false, visualFlash: false,
  },
  hearing: {
    fontSize: "88px", highContrast: true, audioAlert: false, visualFlash: true,
  },
  visual: {
    fontSize: "72px", highContrast: true, audioAlert: true, visualFlash: false,
  },
  mobility: {
    fontSize: "72px", highContrast: false, audioAlert: false, visualFlash: false,
  },
  communication: {
    fontSize: "72px", highContrast: false, audioAlert: false, visualFlash: false,
  },
};

export default function TrackingPage() {
  const { token }                    = useParams();
  const [data,    setData]           = useState(null);
  const [loading, setLoading]        = useState(true);
  const [error,   setError]          = useState(null);
  const [lastUpdated, setLastUpdated]= useState(null);
  const [flash,   setFlash]          = useState(false);
  const timerRef   = useRef(null);
  const mountedRef = useRef(true);
  const prevStatus = useRef(null);

  const poll = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const { data: res } = await publicQueue.trackTicket(token);
      if (mountedRef.current) {
        // Detect transition to "serving" for visual/hearing flash + audio
        if (prevStatus.current !== "serving" && res.status === "serving") {
          const mode = res.accessibility_mode || "none";
          if (MODE_CONFIG[mode]?.visualFlash) triggerFlash();
          if (MODE_CONFIG[mode]?.audioAlert)  speakAlert(res);
        }
        prevStatus.current = res.status;
        setData(res);
        setError(null);
        setLastUpdated(new Date());
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e.response?.status === 404
          ? "Ticket not found. Please check your tracking link."
          : "Unable to load ticket status. Please try again.");
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    mountedRef.current = true;
    poll(true);
    timerRef.current = setInterval(() => poll(false), POLL_INTERVAL);
    return () => {
      mountedRef.current = false;
      clearInterval(timerRef.current);
    };
  }, [poll]);

  useEffect(() => {
    if (data?.status === "completed" || data?.status === "abandoned" || data?.status === "expired") {
      clearInterval(timerRef.current);
    }
  }, [data?.status]);

  const triggerFlash = () => {
    setFlash(true);
    setTimeout(() => setFlash(false), 3000);
  };

  const speakAlert = (ticket) => {
    if (!window.speechSynthesis) return;
    const lang = ticket.language || "en";
    const text = lang === "tw"
      ? `Ticket number ${ticket.ticket_number}. Kɔ ${ticket.current_counter || ticket.current_station} wɔ sɔn.`
      : `Your number ${ticket.ticket_number} is now being called. Please go to ${ticket.current_counter || ticket.current_station}.`;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate  = 0.9;
    utter.volume = 1;
    utter.lang  = lang === "tw" ? "ak-GH" : "en-GH";
    window.speechSynthesis.speak(utter);
  };

  if (loading && !data) return <Shell flash={false}><LoadingView /></Shell>;
  if (error)           return <Shell flash={false}><ErrorView message={error} /></Shell>;
  if (!data)           return null;

  const mode   = data.accessibility_mode || "none";
  const modeCfg = MODE_CONFIG[mode] || MODE_CONFIG.none;
  const STATUS = getStatusConfig(data.status);

  return (
    <Shell flash={flash && data.status === "serving"} mode={mode}>
      {/* Hearing/Visual: giant "YOUR TURN" banner when serving */}
      {(mode === "hearing" || mode === "visual") && data.status === "serving" && (
        <div style={T.urgentBanner}>
          <div style={T.urgentTitle}>🔔 YOUR TURN NOW</div>
          <div style={T.urgentSub}>
            Please go to{" "}
            <strong>{data.current_counter || data.current_station}</strong>
          </div>
        </div>
      )}

      {/* Simple mode: extra-large card */}
      <div style={{ ...T.card, ...(modeCfg.highContrast ? T.cardHighContrast : {}) }}>
        {/* Header */}
        <div style={T.cardHeader}>
          <div style={T.poweredBy}>EliyaQ Queue Tracking</div>
          {lastUpdated && (
            <div style={T.lastUpdated}>Updated {formatTime(lastUpdated)}</div>
          )}
        </div>

        {/* Accessibility mode banner */}
        {mode !== "none" && <AccessModeBanner mode={mode} />}

        {/* Status banner */}
        <div style={{ ...T.statusBanner, background: STATUS.bg, border: `1px solid ${STATUS.border}` }}>
          <span style={{ fontSize: "28px" }}>{STATUS.icon}</span>
          <div>
            <div style={{ ...T.statusLabel, color: STATUS.color }}>{STATUS.label}</div>
            <div style={{ fontSize: "13px", color: "#64748B", marginTop: "2px" }}>{STATUS.desc}</div>
          </div>
        </div>

        {/* Ticket number */}
        <div style={T.ticketBlock}>
          <div style={T.ticketNumLabel}>Your Queue Number</div>
          <div style={{ ...T.ticketNum, fontSize: modeCfg.fontSize }}>
            {data.ticket_number}
          </div>
          <div style={T.patientName}>{data.patient_name}</div>
          {data.priority !== "normal" && (
            <div style={{
              ...T.priorityChip,
              background: data.priority === "emergency" ? "rgba(239,68,68,0.1)" : "rgba(234,179,8,0.1)",
              color:      data.priority === "emergency" ? "#EF4444" : "#EAB308",
            }}>
              {data.priority === "emergency" ? "🚨 Emergency" : "⚡ Priority"}
            </div>
          )}
        </div>

        {/* Position + Wait */}
        {(data.status === "waiting" || data.status === "serving") && (
          <div style={T.infoRow}>
            {data.position != null && data.status === "waiting" && (
              <InfoBox icon="🎯" label="Position in Queue" value={data.position}
                sub={data.position === 1 ? "You're next!" : `${data.position - 1} ahead of you`}
                highlight={data.position <= 3} large={modeCfg.fontSize !== "72px"} />
            )}
            {data.estimated_wait != null && data.status === "waiting" && (
              <InfoBox icon="⏱" label="Est. Wait" value={`${data.estimated_wait}`}
                sub="minutes" large={modeCfg.fontSize !== "72px"} />
            )}
            {data.status === "serving" && (
              <InfoBox icon="🔔" label="Status" value="Now!" sub="Being served" highlight large={modeCfg.fontSize !== "72px"} />
            )}
          </div>
        )}

        {/* Current station */}
        {data.current_station && (
          <div style={T.stationBox}>
            <div style={T.stationLabel}>
              {data.status === "serving" ? "Being served at" : "Currently at"}
            </div>
            <div style={{ ...T.stationName, fontSize: modeCfg.fontSize !== "72px" ? "22px" : "18px" }}>
              {data.current_station}
            </div>
            {data.current_counter && (
              <div style={T.counterName}>Counter: {data.current_counter}</div>
            )}
          </div>
        )}

        {/* Mobility assistance note */}
        {mode === "mobility" && data.status === "waiting" && (
          <div style={T.mobilityNote}>
            <div style={{ fontSize: "20px", marginBottom: "6px" }}>♿</div>
            <div style={{ fontSize: "14px", fontWeight: "600", color: "#60A5FA" }}>
              Mobility Assistance Requested
            </div>
            <div style={{ fontSize: "13px", color: "#64748B", marginTop: "4px", lineHeight: 1.5 }}>
              Staff have been notified. When it is your turn, a staff member will come to you — you do not need to walk to the counter.
            </div>
          </div>
        )}

        {/* Communication assistance note */}
        {mode === "communication" && (
          <div style={T.commNote}>
            <div style={{ fontSize: "20px", marginBottom: "6px" }}>💬</div>
            <div style={{ fontSize: "14px", fontWeight: "600", color: "#A78BFA" }}>
              Communication Assistance
            </div>
            <div style={{ fontSize: "13px", color: "#64748B", marginTop: "4px", lineHeight: 1.5 }}>
              Staff have been informed. A communication support card will be ready when you are called.
            </div>
          </div>
        )}

        {/* Journey trail */}
        {data.journey?.length > 0 && (
          <div style={T.journeySection}>
            <div style={T.journeyTitle}>Your Visit Journey</div>
            <div style={T.journey}>
              {data.journey.map((j, i) => (
                <div key={i} style={T.journeyStep}>
                  <div style={T.journeyLeft}>
                    <div style={{
                      ...T.journeyDot,
                      background: j.status === "completed" ? "#22C55E" :
                                  j.status === "serving"   ? "#2563EB" : "#334155",
                      boxShadow: j.status === "serving" ? "0 0 0 4px rgba(37,99,235,0.2)" : "none",
                    }} />
                    {i < data.journey.length - 1 && <div style={T.journeyLine} />}
                  </div>
                  <div style={T.journeyContent}>
                    <div style={T.journeyStation}>{j.station_name}</div>
                    {j.outcome_label && <div style={T.journeyOutcome}>→ {j.outcome_label}</div>}
                    {j.wait_minutes != null && <div style={T.journeyMeta}>Wait: {j.wait_minutes} min</div>}
                  </div>
                  <div style={{ ...T.journeyStatus, color: j.status === "completed" ? "#22C55E" : "#64748B" }}>
                    {j.status === "completed" ? "✓ Done" : j.status === "serving" ? "In progress" : "Waiting"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completion */}
        {data.status === "completed" && (
          <div style={T.completedBox}>
            <div style={{ fontSize: "32px", marginBottom: "8px" }}>🎉</div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#22C55E", marginBottom: "4px" }}>Visit Complete</div>
            <div style={{ fontSize: "13px", color: "#64748B" }}>Thank you for your visit. We hope you received great care.</div>
          </div>
        )}

        {(data.status === "abandoned" || data.status === "expired") && (
          <div style={T.abandonedBox}>
            <div style={{ fontSize: "13px", color: "#EF4444" }}>
              {data.status === "expired"
                ? "This ticket has expired at end of day. Please collect a new ticket."
                : "This ticket has been marked as abandoned. Please visit the front desk if you are still present."}
            </div>
          </div>
        )}

        {/* Live indicator */}
        {!["completed", "abandoned", "expired"].includes(data.status) && (
          <div style={T.liveBar}>
            <span style={T.liveDot} />
            <span style={{ fontSize: "12px", color: "#334155" }}>
              Live — updates automatically every 10 seconds
            </span>
          </div>
        )}
      </div>
    </Shell>
  );
}

// ── Accessibility mode banner ─────────────────────────────────────────────────
function AccessModeBanner({ mode }) {
  const config = {
    simple:        { icon: "📖", label: "Easy Reading Mode",         color: "#60A5FA", bg: "rgba(37,99,235,0.1)"  },
    hearing:       { icon: "👂", label: "Hearing Impairment Mode",   color: "#A78BFA", bg: "rgba(124,58,237,0.1)" },
    visual:        { icon: "👁",  label: "Visual Assistance Mode",    color: "#34D399", bg: "rgba(52,211,153,0.1)" },
    mobility:      { icon: "♿", label: "Mobility Assistance",        color: "#FBBF24", bg: "rgba(251,191,36,0.1)" },
    communication: { icon: "💬", label: "Communication Assistance",  color: "#F472B6", bg: "rgba(244,114,182,0.1)" },
  }[mode];
  if (!config) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px",
      padding: "10px 16px", borderRadius: "10px",
      background: config.bg, margin: "0 20px",
    }}>
      <span style={{ fontSize: "20px" }}>{config.icon}</span>
      <span style={{ fontSize: "13px", fontWeight: "600", color: config.color }}>{config.label}</span>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function getStatusConfig(status) {
  const map = {
    waiting:     { icon: "⏳", label: "Waiting",      desc: "Please wait. We'll call your number soon.",   color: "#EAB308", bg: "rgba(234,179,8,0.08)",   border: "rgba(234,179,8,0.2)" },
    serving:     { icon: "🔔", label: "Being Served", desc: "Please go to your counter now!",               color: "#2563EB", bg: "rgba(37,99,235,0.1)",    border: "rgba(37,99,235,0.3)" },
    transferred: { icon: "➡️", label: "Transferred",  desc: "You've been sent to another station.",         color: "#A78BFA", bg: "rgba(124,58,237,0.08)",  border: "rgba(124,58,237,0.2)" },
    completed:   { icon: "✅", label: "Complete",      desc: "Your visit is complete. Thank you!",           color: "#22C55E", bg: "rgba(34,197,94,0.08)",   border: "rgba(34,197,94,0.2)" },
    abandoned:   { icon: "❌", label: "Abandoned",     desc: "This ticket has been marked as abandoned.",    color: "#EF4444", bg: "rgba(239,68,68,0.08)",   border: "rgba(239,68,68,0.2)" },
    expired:     { icon: "🕐", label: "Expired",       desc: "This ticket expired at end of day.",           color: "#64748B", bg: "rgba(100,116,139,0.08)", border: "rgba(100,116,139,0.2)" },
  };
  return map[status] || map.waiting;
}

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function InfoBox({ icon, label, value, sub, highlight, large }) {
  return (
    <div style={{
      flex: 1, textAlign: "center", padding: large ? "20px 14px" : "16px 12px",
      background: highlight ? "rgba(37,99,235,0.1)" : "rgba(30,41,59,0.5)",
      border: `1px solid ${highlight ? "rgba(37,99,235,0.3)" : "rgba(51,65,85,0.4)"}`,
      borderRadius: "12px",
    }}>
      <div style={{ fontSize: "20px", marginBottom: "4px" }}>{icon}</div>
      <div style={{ fontSize: "11px", color: "#64748B", fontWeight: "600", letterSpacing: "0.06em", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: large ? "40px" : "28px", fontWeight: "800", color: highlight ? "#60A5FA" : "#E2E8F0", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: "11px", color: "#64748B", marginTop: "4px" }}>{sub}</div>
    </div>
  );
}

function LoadingView() {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ width: "40px", height: "40px", border: "3px solid rgba(37,99,235,0.2)", borderTopColor: "#2563EB", borderRadius: "50%", animation: "eq-spin 0.7s linear infinite", margin: "0 auto 16px" }} />
      <div style={{ color: "#64748B", fontSize: "15px" }}>Loading your ticket…</div>
    </div>
  );
}

function ErrorView({ message }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 24px" }}>
      <div style={{ fontSize: "44px", marginBottom: "16px" }}>🔍</div>
      <div style={{ fontSize: "18px", fontWeight: "700", color: "#E2E8F0", marginBottom: "8px" }}>Ticket Not Found</div>
      <div style={{ fontSize: "14px", color: "#64748B", lineHeight: "1.6" }}>{message}</div>
    </div>
  );
}

function Shell({ children, flash, mode }) {
  return (
    <div style={{
      minHeight: "100vh",
      background: flash ? "#1a0a00" : (mode === "simple" || mode === "hearing" || mode === "visual") ? "#020810" : "#060D1F",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
      padding: "32px 16px",
      transition: "background 0.3s",
      animation: flash ? "eq-flash 0.5s ease-in-out 4" : "none",
    }}>
      <div style={{ width: "100%", maxWidth: "480px" }}>{children}</div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const T = {
  urgentBanner: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
    background: "linear-gradient(135deg,#1D4ED8,#7C3AED)",
    padding: "20px 24px", textAlign: "center",
    boxShadow: "0 4px 30px rgba(37,99,235,0.5)",
  },
  urgentTitle: { fontSize: "28px", fontWeight: "900", color: "#fff", letterSpacing: "-0.5px" },
  urgentSub:   { fontSize: "16px", color: "rgba(255,255,255,0.85)", marginTop: "4px" },

  card: {
    background: "rgba(15,23,42,0.9)", border: "1px solid rgba(37,99,235,0.2)",
    borderRadius: "20px", overflow: "hidden",
    boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
  },
  cardHighContrast: {
    background: "#000", border: "2px solid #fff",
    boxShadow: "0 0 0 2px #fff",
  },
  cardHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "14px 20px", borderBottom: "1px solid rgba(37,99,235,0.1)",
    background: "rgba(37,99,235,0.05)",
  },
  poweredBy:   { fontSize: "12px", fontWeight: "700", color: "#2563EB", letterSpacing: "0.05em" },
  lastUpdated: { fontSize: "11px", color: "#334155" },
  statusBanner: {
    display: "flex", alignItems: "center", gap: "14px",
    margin: "20px 20px 0", borderRadius: "12px", padding: "14px 18px",
  },
  statusLabel: { fontSize: "16px", fontWeight: "700" },
  ticketBlock: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
    padding: "24px 20px 20px", textAlign: "center",
  },
  ticketNumLabel: { fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.1em", textTransform: "uppercase" },
  ticketNum:      { fontWeight: "900", color: "#fff", letterSpacing: "-2px", lineHeight: 1, fontVariantNumeric: "tabular-nums" },
  patientName:    { fontSize: "18px", fontWeight: "600", color: "#94A3B8" },
  priorityChip:   { borderRadius: "8px", padding: "4px 12px", fontSize: "13px", fontWeight: "700" },
  infoRow:        { display: "flex", gap: "10px", padding: "0 20px 16px" },
  stationBox: {
    margin: "0 20px 16px", background: "rgba(30,41,59,0.6)",
    border: "1px solid rgba(37,99,235,0.15)", borderRadius: "12px",
    padding: "14px 18px", textAlign: "center",
  },
  stationLabel: { fontSize: "11px", fontWeight: "600", color: "#475569", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "4px" },
  stationName:  { fontSize: "18px", fontWeight: "700", color: "#E2E8F0" },
  counterName:  { fontSize: "13px", color: "#64748B", marginTop: "3px" },
  mobilityNote: {
    margin: "0 20px 16px", background: "rgba(251,191,36,0.07)",
    border: "1px solid rgba(251,191,36,0.2)", borderRadius: "12px",
    padding: "16px 18px", textAlign: "center",
  },
  commNote: {
    margin: "0 20px 16px", background: "rgba(167,139,250,0.07)",
    border: "1px solid rgba(167,139,250,0.2)", borderRadius: "12px",
    padding: "16px 18px", textAlign: "center",
  },
  journeySection: { padding: "0 20px 16px" },
  journeyTitle:   { fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "12px" },
  journey:        { display: "flex", flexDirection: "column" },
  journeyStep:    { display: "flex", gap: "12px", paddingBottom: "4px" },
  journeyLeft:    { display: "flex", flexDirection: "column", alignItems: "center", width: "16px", flexShrink: 0 },
  journeyDot:     { width: "14px", height: "14px", borderRadius: "50%", flexShrink: 0, marginTop: "3px", transition: "all 0.3s" },
  journeyLine:    { width: "2px", flex: 1, background: "rgba(37,99,235,0.15)", margin: "4px 0", minHeight: "20px" },
  journeyContent: { flex: 1, paddingBottom: "14px", minWidth: 0 },
  journeyStation: { fontSize: "14px", fontWeight: "600", color: "#E2E8F0" },
  journeyOutcome: { fontSize: "12px", color: "#64748B", marginTop: "2px" },
  journeyMeta:    { fontSize: "11px", color: "#334155", marginTop: "2px" },
  journeyStatus:  { fontSize: "11px", fontWeight: "600", flexShrink: 0, paddingTop: "4px" },
  completedBox: {
    margin: "0 20px 16px", background: "rgba(34,197,94,0.08)",
    border: "1px solid rgba(34,197,94,0.2)", borderRadius: "12px",
    padding: "24px", textAlign: "center",
  },
  abandonedBox: {
    margin: "0 20px 16px", background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.2)", borderRadius: "12px",
    padding: "14px 18px",
  },
  liveBar: {
    display: "flex", alignItems: "center", gap: "8px", justifyContent: "center",
    padding: "12px 20px", borderTop: "1px solid rgba(37,99,235,0.08)",
    background: "rgba(37,99,235,0.03)",
  },
  liveDot: {
    width: "7px", height: "7px", borderRadius: "50%",
    background: "#22C55E", boxShadow: "0 0 6px #22C55E",
    flexShrink: 0, display: "inline-block",
    animation: "eq-pulse 2s ease-in-out infinite",
  },
};
